import { useTranslation } from 'react-i18next';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useCurrency } from '@/hooks/useCurrency';
import type { CreditTermsDraft } from '../creditTerms';

interface CreditTermsPanelProps {
  value: CreditTermsDraft;
  onChange: (next: CreditTermsDraft) => void;
  /** What the plan will finance: the part of the total the tenders leave. */
  financedMinor: number;
  maxInstallments: number;
  defaultCreditDays: number;
  /** Shown only when the limit is exceeded, the policy allows an override and the user holds sales.credit_override. */
  showOverride: boolean;
  /** i18n keys by field. */
  errors: Record<string, string>;
}

/**
 * How a balance left after checkout is paid: one deferred payment or a schedule.
 *
 * Only the terms; the server builds the schedule, checks the customer's limit and refuses what policy forbids. Every field has the tenant's default, so leaving it as it is means "pay the rest by the default date".
 */
export function CreditTermsPanel({
  value,
  onChange,
  financedMinor,
  maxInstallments,
  defaultCreditDays,
  showOverride,
  errors,
}: CreditTermsPanelProps) {
  const { t } = useTranslation();
  const { format } = useCurrency();
  const edit = (patch: Partial<CreditTermsDraft>) => onChange({ ...value, ...patch });
  const error = (field: string) => (errors[field] ? t(errors[field] as string) : undefined);

  return (
    <div className="space-y-3">
      <p className="text-ink text-sm">
        {t('sales.creditTerms.financed')}{' '}
        <span className="font-semibold" dir="ltr">
          {format(financedMinor)}
        </span>
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Input
          label={t('sales.creditTerms.installmentCount')}
          hint={t('sales.creditTerms.installmentCountHint', { max: maxInstallments })}
          inputMode="numeric"
          dir="ltr"
          className="rtl:text-right"
          value={value.installmentCount}
          error={error('installmentCount')}
          onChange={(event) => edit({ installmentCount: event.target.value })}
        />
        <FormField label={t('sales.creditTerms.interval')}>
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={value.interval}
              options={(['MONTHLY', 'WEEKLY', 'DAYS'] as const).map((interval) => ({
                value: interval,
                label: t(`sales.creditTerms.intervals.${interval}`),
              }))}
              onChange={(event) =>
                edit({ interval: event.target.value as CreditTermsDraft['interval'] })
              }
            />
          )}
        </FormField>
        {value.interval === 'DAYS' ? (
          <Input
            label={t('sales.creditTerms.intervalDays')}
            inputMode="numeric"
            dir="ltr"
            className="rtl:text-right"
            value={value.intervalDays}
            error={error('intervalDays')}
            onChange={(event) => edit({ intervalDays: event.target.value })}
          />
        ) : null}
        <Input
          type="date"
          label={t('sales.creditTerms.firstDueDate')}
          hint={t('sales.creditTerms.firstDueDateHint', { days: defaultCreditDays })}
          dir="ltr"
          value={value.firstDueDate}
          error={error('firstDueDate')}
          onChange={(event) => edit({ firstDueDate: event.target.value })}
        />
      </div>
      {showOverride ? (
        <Input
          label={t('sales.creditTerms.overrideReason')}
          hint={t('sales.creditTerms.overrideReasonHint')}
          value={value.overrideReason}
          maxLength={500}
          error={error('overrideReason')}
          onChange={(event) => edit({ overrideReason: event.target.value })}
        />
      ) : null}
    </div>
  );
}
